import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { LoadingOverlay } from '@travelity/ui/loading-overlay';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateProductDetails } from '@travelity/api/services';
import { useForm } from '@travelity/form/use-form';
import { TextArea, TextField } from '@travelity/form/controls';
import { ReadonlyTextarea } from '@components/readonly-textarea';
import AccordionButtons from '@/views/products/components/accordion-buttons';
import { useToast } from '@/hooks';
import type { PackageSidebarItemsProps } from './types';

type DetailsFormValues = {
    name: string;
    description: string;
};

export const PackageDetails = ({ product }: PackageSidebarItemsProps) => {
    const { t } = useTranslation('product');
    const { showSuccess } = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const { mutate: updateDetails, isLoading: isUpdatingDetails } =
        useUpdateProductDetails();
    const {
        Form: DetailsForm,
        reset: resetDetails,
        formState: { errors: detailsErrors },
    } = useForm<DetailsFormValues>({
        defaultValues: {
            name: product.name,
            description: product.description,
        },
        onSubmit: useCallback(
            (data: DetailsFormValues, e?: React.BaseSyntheticEvent) => {
                e?.preventDefault();
                updateDetails(
                    {
                        productId: product.id,
                        data,
                    },
                    {
                        onSuccess: () => {
                            setIsEditing(false);
                            showSuccess('UPDATE_PACKAGE_DETAILS_SUCCESS', {
                                name: data.name,
                            });
                        },
                    }
                );
            },
            [updateDetails, product.id, showSuccess]
        ),
        mode: 'onChange',
    });

    const onCancel = () => {
        resetDetails();
        setIsEditing(false);
    };

    return (
        <DetailsForm id="productInfo">
            <Accordion
                disableGutters
                expanded={isExpanded}
                onChange={() => !isEditing && setIsExpanded(!isExpanded)}
            >
                <AccordionSummary>
                    {t('Product Details')}
                    {isExpanded && (
                        <AccordionButtons
                            editing={isEditing}
                            saving={isUpdatingDetails}
                            errors={detailsErrors}
                            onEditCancel={onCancel}
                            onEdit={() => setIsEditing(true)}
                        />
                    )}
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                    }}
                >
                    <TextField
                        placeholder="Type Product Name or Use Generated Name"
                        name="name"
                        label="Product Name"
                        required
                        showErrors={!!detailsErrors}
                        hideHelperText
                        disabled
                    />
                    {!isEditing ? (
                        <ReadonlyTextarea
                            sx={{
                                width: { xs: '100%', sm: '388px' },
                                opacity: 0.8,
                            }}
                            label="Description"
                            content={product.description}
                        />
                    ) : (
                        <TextArea
                            placeholder="Type Description"
                            name="description"
                            showErrors={!!detailsErrors}
                            hideHelperText
                            label="Description"
                            required
                        />
                    )}
                    {isUpdatingDetails && <LoadingOverlay />}
                </AccordionDetails>
            </Accordion>
        </DetailsForm>
    );
};
